import i18n from 'helpers/i18n';
import messages from './messages';
import { clsx } from 'clsx';
import { useState } from 'react';
import { TextButton } from 'components/Organization/TextButton';
import { PrimaryButton } from 'components/Organization/PrimaryButton';
import { IconButton } from '../IconButton';

interface EditableFieldProps {
  /** Field label. */
  label: string;

  /** Field value to be displayed in non edit mode. */
  value: React.ReactNode;

  /** When `true`, label is shown. */
  showLabel?: boolean;

  /** When `true`, field uses text-sm, icons instead of buttons, and flex-row alignment. */
  small?: boolean;

  /** When `true`, top-border is added to the field. */
  border?: boolean;

  /** Additional classes. */
  className?: string;

  /** Initial value for the control. */
  initialValue?: any;

  /** When `true`, the field is disabled. */
  disabled?: boolean;

  /** Callback fired when the field is saved. */
  onSave?: (value: any) => void | boolean | Promise<void | boolean>;

  /** Custom control to be rendered in edit mode. */
  control?: (options: {
    value: any;
    setValue: (value: any) => void;
  }) => React.ReactNode;
}

export function EditableField({
  label,
  value,
  showLabel = true,
  small = false,
  onSave,
  control,
  className,
  initialValue,
  border = true,
  disabled = false
}: EditableFieldProps) {
  const [internalValue, setInternalValue] = useState<any>(initialValue);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const classes = clsx(
    'flex',
    small ? 'items-center' : 'py-6 gap-8 justify-between',
    border && 'border-t border-[#D7DFE8]',
    className
  );

  const handleSave = async () => {
    if (isSaving) {
      return;
    }

    setIsSaving(true);
    const isSuccess = await Promise.resolve(onSave?.(internalValue));
    setIsSaving(false);

    if (typeof isSuccess === 'boolean' && !isSuccess) {
      return;
    } else {
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    if (isSaving) {
      return;
    }

    setInternalValue(initialValue);
    setIsEditing(false);
  };

  return (
    <div className={classes}>
      {isEditing ? (
        <div
          className={clsx(
            'flex',
            !small ? 'w-full flex-col gap-4' : 'flex-row items-center'
          )}
        >
          {control?.({ value: internalValue, setValue: setInternalValue })}

          {!small ? (
            <div className="flex justify-end gap-6">
              <TextButton size="small" onClick={handleCancel}>
                {i18n.ft(messages.cancel)}
              </TextButton>
              <PrimaryButton size="small" onClick={handleSave}>
                {i18n.ft(messages.save)}
              </PrimaryButton>
            </div>
          ) : (
            <>
              <IconButton
                icon="fa-solid fa-close"
                label={i18n.ft(messages.cancel)}
                onClick={handleCancel}
                className="flex items-start justify-center text-action text-base p-3"
                aria-label={i18n.ft(messages.cancel)}
              />
              <IconButton
                icon="fa-solid fa-check"
                label={i18n.ft(messages.save)}
                onClick={handleSave}
                className="flex items-start justify-center text-action text-base p-3"
                aria-label={i18n.ft(messages.save)}
              />
            </>
          )}
        </div>
      ) : (
        <div>
          {showLabel ? (
            <div className="text-base font-semibold">{label}</div>
          ) : (
            <div className="sr-only">{label}</div>
          )}
          <div className={!small ? 'mt-1.5 text-base' : ''}>
            {value || '--'}
          </div>
        </div>
      )}

      {control && !isEditing && !disabled && (
        <button
          onClick={() => setIsEditing(true)}
          className={clsx(
            'flex items-center justify-center text-action',
            !small ? 'w-10 h-10 shrink-0' : 'pl-2'
          )}
        >
          <i className="fa-solid fa-pencil" />
        </button>
      )}
    </div>
  );
}
